import type { ReactNode } from 'react';
import Link from 'next/link';
import Title from '@firsttable/web-components/atoms/Title';
import { TrackProvider } from '@/components/analytics/context/AnalyticsContext';
import { TrackLink } from '@/components/analytics/hocs/withTrackableNavLinkComponent';

type Suburb = {
  id: number | string;
  parentId?: number | string | null;
  menuTitle: string;
  slug: string;
};

type SuburbDirectoryProps = {
  suburbs: Suburb[];
  locationTitle: string;
  locationId: number | string;
  parentId?: number | string | null;
  parentTitle?: string | null;
  sessionLinks?: ReactNode;
};

export default function SuburbDirectory({
  suburbs,
  locationTitle,
  locationId,
  parentId,
  parentTitle,
  sessionLinks,
}: SuburbDirectoryProps) {
  const available = Array.from(
    new Map(
      suburbs
        .filter(
          (suburb) =>
            String(suburb.id) !== String(locationId) &&
            suburb.menuTitle &&
            /^\/(?!\/)/.test(suburb.slug)
        )
        .map((suburb) => [String(suburb.id), suburb])
    ).values()
  ).sort((a, b) => a.menuTitle.localeCompare(b.menuTitle));
  const children = available.filter((suburb) => String(suburb.parentId) === String(locationId));
  const showSiblings = children.length === 0;
  const locations = showSiblings
    ? available.filter((suburb) => !!parentId && String(suburb.parentId) === String(parentId))
    : children;
  if (!locations.length) {
    return <div className="mx-auto max-w-[1200px] px-4">{sessionLinks}</div>;
  }
  const siblingHeading = parentTitle
    ? `Explore more suburbs in ${parentTitle}`
    : 'Explore more suburbs';

  return (
    <TrackProvider component_name="suburb directory">
      <div
        className="relative mt-12 px-4 py-12 sm:py-20"
        style={{
          background:
            "url('/images/lights.svg?url') no-repeat calc(50% - 700px) -70px, url('/images/glasses.svg?url') no-repeat calc(50% + 750px) calc(100% + 70px), linear-gradient(180deg, #fbf6f0 27.52%, #fff 100%)",
        }}>
        <div className="relative mx-auto max-w-[1200px]">
          {sessionLinks}
          <nav aria-labelledby="suburb-directory-heading">
            <Title
              id="suburb-directory-heading"
              as="h2"
              size="h2"
              mb={32}
              className="!text-xl md:!text-3xl">
              {showSiblings ? siblingHeading : `Explore restaurants around ${locationTitle}`}
            </Title>
            <ul className="m-0 list-none columns-1 gap-8 p-0 sm:columns-2 lg:columns-4">
              {locations.map((suburb) => (
                <li key={suburb.id} className="mb-3 break-inside-avoid">
                  <TrackProvider link_name={`${suburb.menuTitle} suburb directory link`}>
                    <TrackLink>
                      <Link
                        href={suburb.slug}
                        prefetch={false}
                        className="inline-block py-1 text-neutral-800 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4">
                        {suburb.menuTitle}
                      </Link>
                    </TrackLink>
                  </TrackProvider>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>
    </TrackProvider>
  );
}
